import { ConsentGate } from './features/auth/ConsentGate';
import { Impressum } from './screens/legal/Impressum';
import { Datenschutz } from './screens/legal/Datenschutz';
import { Review } from './screens/Review';
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { track } from './lib/telemetry';
import { useOutcomeTracking } from './lib/track-game';
import { S as liveS } from './engine/state';
import { useGame, useS, setCloudSave } from './store/game';
import { AuthProvider, useAuth } from './features/auth/AuthProvider';
import { Login } from './features/auth/Login';
import { Leaderboard } from './features/leaderboard/Leaderboard';
import { submitRun } from './features/leaderboard/api';
import { Lobby, useSync } from './features/multiplayer/Lobby';
import { resume, status } from './features/multiplayer/sync';
import { VersusView, chancellorScreens } from './features/multiplayer/Versus';
import { journal } from './engine/actions';
import { tooNew, RULES } from './engine/version';
import { cloudSaveDebounced, cloudLoad } from './features/saves/cloud';
import { mergeCloud, SaveChoice } from './features/saves/merge';
import { Coach, HelpButton } from './features/tutorial/Coach';
import { Hilfe } from './screens/Hilfe';
import { Moments } from './features/moments/Moments';
import { Program } from './screens/Program';
import { Settings } from './screens/Settings';
import { Night } from './screens/Night';
import { Talks } from './screens/Talks';
import { Campaign } from './screens/Campaign';
import { Govern } from './screens/govern/Govern';
import { End } from './screens/End';
import { Landing } from './screens/Landing';
import { DiffPick } from './screens/DiffPick';
import { usePageMeta } from './lib/seo';

/** Kurze Meldung unten (Wahl gewonnen, Gesetz beschlossen …). Verschwindet nach ein paar Sekunden oder per Klick. */
function Toast() {
  const S = useS(); const [shown, setShown] = useState<any>(null);
  // Beim Laden eines Spielstands die alte Meldung nicht erneut zeigen
  const last = useRef<unknown>(S.toast?.id);
  const id = S.toast?.id;
  useEffect(() => {
    if (!S.toast || id === last.current) return;
    last.current = id; setShown(S.toast);
    const t = setTimeout(() => setShown(null), 5000);
    return () => clearTimeout(t);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!shown) return null;
  return <div className={'toast ' + (shown.k || '')} role="status" onClick={() => setShown(null)} title="Schließen">{shown.t}</div>;
}

/** Spielstand oder Partie aus einer neueren Version: Seite neu laden statt mit alten Regeln weiterzurechnen. */
function UpdateNotice() {
  return <section className="screen"><div><span className="label">Neue Version</span><h2>Kabinett wurde aktualisiert</h2></div>
    <p>Dieser Spielstand stammt aus einer neueren Version des Spiels. Lade die Seite neu, dann geht es direkt weiter.</p>
    <div className="actions"><button type="button" className="yes" onClick={() => window.location.reload()}>Seite neu laden</button></div></section>;
}

function Game() {
  const S = useS(); const { newGame } = useGame();
  useOutcomeTracking();
  const [cfg, setCfg] = useState(false); const [confirm, setConfirm] = useState(false);
  const [diffStep, setDiffStep] = useState(false);
  useEffect(() => { if (S.phase !== 'program') setDiffStep(false); }, [S.phase]);
  const [note, setNote] = useState<string | null>(null);
  const nav = useNavigate();
  const sync = useSync();
  const gameOver = async () => {
    track('Game.finished', { mode: S.mp ? 'versus' : 'single', reason: S.end || 'unknown', legislatures: S.career.length, share: Math.round(S.final || 0), won: !!S.won });
    if (sync) { nav('/spiel'); return; } // Mehrspieler-Partien kommen (noch) nicht in die Bestenliste
    const moves = journal.slice();
    const r = await submitRun(S, moves);
    if (r.ok) track('Leaderboard.submitted', { verified: !!r.verified });
    if (r.ok) setNote(r.verified ? `Partie geprüft und eingetragen: ${r.score.toLocaleString('de-DE')} Punkte.` : `Partie eingetragen (ungeprüft): ${r.score.toLocaleString('de-DE')} Punkte.`);
    else if (r.reason !== 'offline' && r.reason !== 'nicht angemeldet') setNote('Eintrag in die Bestenliste fehlgeschlagen: ' + r.reason);
    nav('/spiel');
  };
  const top = useRef<HTMLDivElement>(null);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [S.phase, S.editing, S.bview, cfg, diffStep]);
  const versus = !!S.mp && S.mp.mode === 'versus';
  const guest = versus && !chancellorScreens(S);
  const screen = tooNew(S) ? <UpdateNotice /> : S.phase === 'review' && !cfg ? <Review /> : guest ? <VersusView /> : cfg ? <Settings close={() => setCfg(false)} />
    : S.phase === 'program' ? (diffStep ? <DiffPick back={() => setDiffStep(false)} openSettings={() => setCfg(true)} /> : <Program next={() => setDiffStep(true)} />)
    : S.phase === 'night' ? <Night /> : S.phase === 'talks' ? <Talks /> : S.phase === 'campaign' ? <Campaign />
    : S.phase === 'review' ? <Review />
    : S.phase === 'end' ? <End onGameOver={() => { void gameOver(); }} /> : <Govern />;
  return (
    <>
      <div ref={top} className="actions" id="topctl" style={{ justifyContent: 'flex-end' }}>
        {sync && <span className="tag" title="Gemeinsame Partie">Partie {sync.match.code} · {sync.pending ? `${sync.pending} Zug unterwegs` : 'synchron'}</span>}
        {sync?.error && <span className="sub down">{sync.error}</span>}
        {note && <span className="sub">{note} <button type="button" className="small" onClick={() => setNote(null)}>✕</button></span>}
        <HelpButton />
        {!cfg && !guest && <button type="button" className="small" onClick={() => setCfg(true)}>Einstellungen</button>}
        {S.phase !== 'program' && !sync && (confirm
          ? <><span className="sub">Spielstand verwerfen?</span><button type="button" className="small" onClick={() => { if (S.phase !== 'program' && S.phase !== 'end') track('Game.abandoned', { phase: S.phase, legislature: S.leg + 1, month: S.m - S.m0 + 1 }); newGame(); setConfirm(false); }}>Ja, neu starten</button><button type="button" className="small" onClick={() => setConfirm(false)}>Abbrechen</button></>
          : <button type="button" className="small" onClick={() => setConfirm(true)}>Neues Spiel</button>)}
      </div>
      {!sync && <SaveChoice />}
      {!cfg && <Coach />}
      {screen}
      <Moments />
      <Toast />
    </>
  );
}

/** Seitenaufrufe und App-Start zählen (ohne Speicherung im Browser, siehe lib/telemetry). */
function PageTracker({ loggedIn }: { loggedIn: boolean }) {
  const loc = useLocation(); const started = useRef(false);
  useEffect(() => {
    if (!started.current) { started.current = true; track('App.launched', { rules: RULES, returning: !!liveS && liveS.phase !== 'program', loggedIn, multiplayer: !!status() }); }
    track('Page.viewed', { path: loc.pathname });
  }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** Startseite mit „Weiterspielen“, wenn schon eine Partie läuft. */
function LandingRoute() {
  const S = useS();
  const resume = S && (S.phase !== 'program' || S.name !== 'Aufbruch') && !S.mp ? S.name : null;
  return <Landing resume={resume} onStart={() => track('Landing.start', { resume: !!resume })} />;
}

/** Wer schon spielt und die Startadresse aufruft, landet beim ersten Laden direkt im Spiel. */
function PageMeta() { usePageMeta(useLocation().pathname); return null; }

function ResumeRedirect() {
  const loc = useLocation(); const nav = useNavigate(); const done = useRef(false);
  useEffect(() => {
    if (done.current) return; done.current = true;
    if (loc.pathname === '/' && !loc.search && liveS && liveS.phase !== 'program') nav('/spiel', { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function Shell() {
  const { user } = useAuth();
  const { loaded, loadFromStorage } = useGame();
  useEffect(() => { loadFromStorage(); }, [loadFromStorage]);
  // Nur bei echtem Kontowechsel reagieren: Supabase meldet die Sitzung z. B. beim Zurückkehren in den Tab
  // erneut, mit neuem Objekt, aber gleicher Nutzer-ID. Früher wurde dann der Cloud-Spielstand erneut geladen.
  const uid = user?.id ?? null;
  useEffect(() => {
    setCloudSave(uid ? cloudSaveDebounced : null);
    if (!uid) return;
    // Läuft schon eine Mehrspieler-Partie, nie den Einzelspieler-Stand darüber laden.
    if (status()) return;
    cloudLoad().then((s) => { if (!status()) mergeCloud(s); }).finally(() => { void resume(); });
  }, [uid]);
  if (!loaded) return null;
  return (
    <div className="wrap">
      <PageTracker loggedIn={!!user} />
      <ResumeRedirect />
      <PageMeta />
      <header className="top">
        <Link to="/" className="brand" aria-label="Kabinett, zur Startseite"><span className="emblem" aria-hidden="true">K</span><div><span className="brandname">Kabinett</span><div className="sub">Gewinne die Wahl, schmiede eine Koalition und bring eigene Gesetze durch Bundestag und Bundesrat.</div></div></Link>
        <nav className="topnav"><NavLink to="/spiel">Spiel</NavLink><NavLink to="/bestenliste">Bestenliste</NavLink><NavLink to="/mitspielen">Mitspielen</NavLink><NavLink to="/konto">{user ? 'Konto' : 'Anmelden'}</NavLink></nav>
      </header>
      <main>
        <DeletedNotice />
        <ConsentGate><Routes>
          <Route path="/" element={<LandingRoute />} />
          <Route path="/spiel" element={<Game />} />
          <Route path="/bestenliste" element={<Leaderboard />} />
          <Route path="/mitspielen" element={<Lobby />} />
          <Route path="/konto" element={<Login />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/hilfe" element={<Hilfe />} />
        </Routes></ConsentGate>
      </main>
      <footer className="foot">
        <nav aria-label="Rechtliches"><NavLink to="/hilfe">So funktioniert's</NavLink><NavLink to="/impressum">Impressum</NavLink><NavLink to="/datenschutz">Datenschutz</NavLink><a href="https://feature.sevenwhys.com/kabinett" target="_blank" rel="noopener noreferrer" onClick={() => track('Feedback.opened', {})}>Feature anfragen / Bug melden</a></nav>
        <span className="sub">© {new Date().getFullYear()} Kabinett</span>
      </footer>
      <p className="note">Prototyp. Parteien sind fiktiv, Gesetzesideen an reale Debatten angelehnt. Alle Wirkungen sind grobe Spielannahmen, keine Prognosen. Haushaltswerte in Mrd € pro Jahr. Karte der Bundesländer: <a href="https://mapsvg.com/maps/germany" target="_blank" rel="noopener">mapsvg.com</a>, Lizenz CC BY 4.0.</p>
    </div>
  );
}

/** Hinweis nach dem Löschen des Kontos (Weiterleitung mit ?konto=geloescht). */
function DeletedNotice() {
  const [show, setShow] = useState(() => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('konto') === 'geloescht');
  useEffect(() => { if (show) window.history.replaceState(null, '', window.location.pathname); }, [show]);
  if (!show) return null;
  return <div className="card notice" role="status"><b>Dein Konto wurde gelöscht.</b> Alle deine Daten sind aus unserer Datenbank entfernt. <button className="link" onClick={() => setShow(false)}>Schließen</button></div>;
}

export default function App() {
  return <AuthProvider><BrowserRouter><Shell /></BrowserRouter></AuthProvider>;
}
